import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Pencil, Trash2, ImageIcon, Sparkles, GlassWater, CheckCheck } from "lucide-react";
import { AcornPlaceholder, absMediaUrl } from "@/components/AcornPlaceholder";
import VisibilityBreakdownPopover from "./VisibilityBreakdownPopover";

/**
 * Fase 8.5 · /admin/distillati — gestione dedicata ai NON-VINO
 * (distillati / liquori / birre / altro).
 *
 * Riusa gli stessi endpoint admin di /admin/vini, ma con `categoria`
 * di default = non_vino. Le tab in cima permettono di restringere a una
 * sotto-categoria specifica.
 */

const CATEGORY_TABS = [
    { key: "non_vino", label: "Tutti" },
    { key: "distillato", label: "Distillati" },
    { key: "liquore", label: "Liquori" },
    { key: "birra", label: "Birre" },
    { key: "altro", label: "Altro" },
];

const STATUS_LABEL = {
    pending_review: "In revisione",
    da_arricchire: "Da Arricchire",
    arricchito_in_validazione: "In Validazione",
    validato_sommelier: "Validato",
};
const VISIBILITY_LABEL = {
    auto: "Auto",
    force_visible: "Visibile",
    force_hidden: "Nascosto",
};
const SOURCE_LABEL = {
    mexal: "Mexal",
    file: "File",
    photo: "Foto",
    manual: "Manuale",
};
const QUALITY_LABEL = {
    no_price: "Senza prezzo",
    hidden_from_public: "Nascosti al pubblico",
    no_producer: "Senza produttore",
};

export default function AdminDistillati() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [publicCount, setPublicCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(new Set());
    const [deleting, setDeleting] = useState(null);
    const [imgSearching, setImgSearching] = useState(null);
    const [validating, setValidating] = useState(false);

    // Filtri (persistiti in localStorage per continuità sessione admin)
    const [categoria, setCategoria] = useState(() => localStorage.getItem("admin_dist_cat") || "non_vino");
    const [q, setQ] = useState(() => localStorage.getItem("admin_dist_q") || "");
    const [debouncedQ, setDebouncedQ] = useState(q);
    const [statusFilter, setStatusFilter] = useState(() => localStorage.getItem("admin_dist_status") || "");
    const [visibilityFilter, setVisibilityFilter] = useState(() => localStorage.getItem("admin_dist_vis") || "");
    const [sourceFilter, setSourceFilter] = useState(() => localStorage.getItem("admin_dist_source") || "");
    const [qualityFilter, setQualityFilter] = useState(() => localStorage.getItem("admin_dist_quality") || "");
    const [orterieOnly, setOrterieOnly] = useState(() => localStorage.getItem("admin_dist_orterie") === "1");

    useEffect(() => { localStorage.setItem("admin_dist_cat", categoria); }, [categoria]);
    useEffect(() => { localStorage.setItem("admin_dist_q", q); }, [q]);
    useEffect(() => { localStorage.setItem("admin_dist_status", statusFilter); }, [statusFilter]);
    useEffect(() => { localStorage.setItem("admin_dist_vis", visibilityFilter); }, [visibilityFilter]);
    useEffect(() => { localStorage.setItem("admin_dist_source", sourceFilter); }, [sourceFilter]);
    useEffect(() => { localStorage.setItem("admin_dist_quality", qualityFilter); }, [qualityFilter]);
    useEffect(() => { localStorage.setItem("admin_dist_orterie", orterieOnly ? "1" : "0"); }, [orterieOnly]);

    useEffect(() => {
        const h = setTimeout(() => setDebouncedQ(q.trim()), 300);
        return () => clearTimeout(h);
    }, [q]);

    const load = () => {
        setLoading(true);
        const params = { limit: 200, categoria };
        if (debouncedQ) params.q = debouncedQ;
        if (statusFilter) params.status = statusFilter;
        if (visibilityFilter) params.visibility = visibilityFilter;
        if (sourceFilter) params.source = sourceFilter;
        if (qualityFilter) params.filter = qualityFilter;
        api.get("/admin/wines", { params })
            .then(({ data }) => {
                let filtered = data.items;
                if (orterieOnly) {
                    filtered = filtered.filter((w) => (w.producer || "").toLowerCase().includes("orterie"));
                }
                setItems(filtered);
                setTotal(data.total);
                setPublicCount(data.public_count || 0);
                setSelected(new Set());
            })
            .catch(() => toast.error("Errore nel caricamento"))
            .finally(() => setLoading(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(load, [categoria, debouncedQ, statusFilter, visibilityFilter, sourceFilter, qualityFilter, orterieOnly]);

    const toggleSel = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelected(next);
    };
    const toggleAll = () => {
        if (selected.size === items.length) setSelected(new Set());
        else setSelected(new Set(items.map((w) => w.id)));
    };

    const doDelete = async (id) => {
        try {
            await api.delete(`/admin/wines/${id}`);
            toast.success("Prodotto eliminato");
            load();
        } catch { toast.error("Eliminazione fallita"); } finally { setDeleting(null); }
    };

    const searchImage = async (id) => {
        setImgSearching(id);
        try {
            await api.post(`/admin/wines/${id}/image/search`);
            toast.success("Immagine trovata");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Ricerca fallita — carica manualmente");
        } finally { setImgSearching(null); }
    };

    /**
     * Fase 8.7 · Validazione — riusa `/admin/ai/validate-wines` (bulk).
     * L'endpoint è category-agnostic: nessun vincolo su tipologia/uvaggio.
     * `validato_sommelier` sblocca l'esposizione al pubblico delle note
     * organolettiche AI-generate (governate dal flag globale
     * `show_unvalidated_ai_notes`, oggi OFF).
     */
    const validateSingle = async (id) => {
        setValidating(true);
        try {
            await api.post("/admin/ai/validate-wines", { wine_ids: [id] });
            toast.success("Prodotto validato");
            load();
        } catch { toast.error("Errore validazione"); } finally { setValidating(false); }
    };
    const bulkValidate = async () => {
        if (selected.size === 0) return;
        setValidating(true);
        try {
            await api.post("/admin/ai/validate-wines", { wine_ids: Array.from(selected) });
            toast.success(`Validati ${selected.size} prodotti`);
            load();
        } catch { toast.error("Errore validazione"); } finally { setValidating(false); }
    };

    const fmtPrice = (w) => {
        const fmt = (w.formats || [])[0] || {};
        const p = fmt.price ?? fmt.base_price_public;
        if (!p || p === 0) return <span className="text-[hsl(var(--muted-foreground))]">—</span>;
        return `€ ${Number(p).toFixed(2)}`;
    };
    const fmtStock = (w) => {
        const totalStock = (w.formats || []).reduce((s, f) => {
            if (f.stock_quantity === null || f.stock_quantity === undefined) return s;
            return s + Number(f.stock_quantity);
        }, null);
        if (totalStock === null) return <span className="text-[hsl(var(--muted-foreground))]">—</span>;
        return totalStock;
    };
    const fmtSize = (w) => (w.formats || []).map((f) => f.size).join(" · ");
    const fmtSKU = (w) => (w.formats || []).map((f) => f.mexal_sku).filter(Boolean).join(" · ");

    return (
        <div data-testid="admin-distillati-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-3xl flex items-center gap-2">
                        <GlassWater className="w-7 h-7" strokeWidth={1.4} />
                        Distillati & Liquori
                    </h1>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 flex items-center gap-3 flex-wrap" data-testid="admin-dist-header-count">
                        <span>
                            {total} prodott{total === 1 ? "o" : "i"}
                            <span className="mx-2 text-[hsl(var(--border))]">·</span>
                            <span className="text-[hsl(var(--primary))]">{publicCount}</span> pubblic{publicCount === 1 ? "o" : "i"}
                            {selected.size > 0 && (
                                <>
                                    <span className="mx-2 text-[hsl(var(--border))]">·</span>
                                    {selected.size} selezionat{selected.size === 1 ? "o" : "i"}
                                </>
                            )}
                        </span>
                        <VisibilityBreakdownPopover
                            categoria={categoria}
                            onJumpToFilter={(k) => setQualityFilter(k)}
                        />
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {selected.size > 0 && (
                        <button
                            className="co-btn-ghost"
                            onClick={bulkValidate}
                            disabled={validating}
                            data-testid="admin-dist-bulk-validate-btn"
                        >
                            <CheckCheck className="w-4 h-4" />
                            {validating ? "Validazione…" : `Valida (${selected.size})`}
                        </button>
                    )}
                    <button
                        className="co-btn-primary"
                        onClick={() => navigate("/admin/vini/nuovo?categoria=distillato")}
                        data-testid="admin-dist-new-btn"
                    >
                        <Plus className="w-4 h-4" /> Nuovo prodotto
                    </button>
                </div>
            </div>

            {/* Category tabs */}
            <div className="flex gap-1 border-b border-[hsl(var(--border))] mb-4 overflow-x-auto">
                {CATEGORY_TABS.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => setCategoria(t.key)}
                        className={`px-4 py-2 text-sm border-b-2 transition-colors whitespace-nowrap ${
                            categoria === t.key
                                ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))]"
                                : "border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"
                        }`}
                        data-testid={`admin-dist-tab-${t.key}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-4 items-center">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" strokeWidth={1.5} />
                    <Input
                        placeholder="Cerca nome, produttore, denominazione, SKU…"
                        value={q} onChange={(e) => setQ(e.target.value)}
                        data-testid="admin-dist-search"
                        className="pl-10 bg-white"
                    />
                </div>
                <button
                    type="button"
                    onClick={() => setOrterieOnly((v) => !v)}
                    className={`text-xs px-3 py-1.5 rounded-sm border whitespace-nowrap ${
                        orterieOnly
                            ? "border-[hsl(var(--primary))] bg-[#FDFBF7] text-[hsl(var(--primary))]"
                            : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"
                    }`}
                    data-testid="admin-dist-orterie-toggle"
                    title="Filtra rapido produttore 'Orterie'"
                >
                    Solo Orterie
                </button>
                <Select value={statusFilter || "__all"} onValueChange={(v) => setStatusFilter(v === "__all" ? "" : v)}>
                    <SelectTrigger className="w-44 bg-white" data-testid="admin-dist-status-filter">
                        <SelectValue placeholder="Stato" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all">Tutti gli stati</SelectItem>
                        {Object.entries(STATUS_LABEL).map(([k, l]) => (
                            <SelectItem key={k} value={k}>{l}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={visibilityFilter || "__all"} onValueChange={(v) => setVisibilityFilter(v === "__all" ? "" : v)}>
                    <SelectTrigger className="w-44 bg-white" data-testid="admin-dist-vis-filter">
                        <SelectValue placeholder="Visibilità" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all">Tutte visibilità</SelectItem>
                        {Object.entries(VISIBILITY_LABEL).map(([k, l]) => (
                            <SelectItem key={k} value={k}>{l}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={sourceFilter || "__all"} onValueChange={(v) => setSourceFilter(v === "__all" ? "" : v)}>
                    <SelectTrigger className="w-40 bg-white" data-testid="admin-dist-source-filter">
                        <SelectValue placeholder="Sorgente" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all">Tutte sorgenti</SelectItem>
                        {Object.entries(SOURCE_LABEL).map(([k, l]) => (
                            <SelectItem key={k} value={k}>{l}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={qualityFilter || "__all"} onValueChange={(v) => setQualityFilter(v === "__all" ? "" : v)}>
                    <SelectTrigger className="w-52 bg-white" data-testid="admin-dist-quality-filter">
                        <SelectValue placeholder="Qualità dati" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all">Tutti i prodotti</SelectItem>
                        {Object.entries(QUALITY_LABEL).map(([k, l]) => (
                            <SelectItem key={k} value={k}>{l}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="bg-white border border-[hsl(var(--border))] rounded-sm overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-10">
                                <Checkbox
                                    checked={selected.size > 0 && selected.size === items.length}
                                    onCheckedChange={toggleAll}
                                    data-testid="admin-dist-toggle-all"
                                />
                            </TableHead>
                            <TableHead>Immagine</TableHead>
                            <TableHead>Nome</TableHead>
                            <TableHead>Produttore</TableHead>
                            <TableHead>Categoria</TableHead>
                            <TableHead>Gradazione</TableHead>
                            <TableHead>Formato/i</TableHead>
                            <TableHead>SKU</TableHead>
                            <TableHead>Prezzo</TableHead>
                            <TableHead>Giacenza</TableHead>
                            <TableHead>Visibilità</TableHead>
                            <TableHead>Stato</TableHead>
                            <TableHead>Sorgente</TableHead>
                            <TableHead className="text-right">Azioni</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && (
                            <TableRow><TableCell colSpan={14} className="text-center py-8 text-[hsl(var(--muted-foreground))]">Caricamento…</TableCell></TableRow>
                        )}
                        {!loading && items.length === 0 && (
                            <TableRow><TableCell colSpan={14} className="text-center py-8 text-[hsl(var(--muted-foreground))]">Nessun prodotto</TableCell></TableRow>
                        )}
                        {items.map((w) => {
                            const thumb = absMediaUrl(w.images?.thumb_url);
                            return (
                                <TableRow key={w.id} data-testid={`admin-dist-row-${w.id}`}>
                                    <TableCell>
                                        <Checkbox
                                            checked={selected.has(w.id)}
                                            onCheckedChange={() => toggleSel(w.id)}
                                            data-testid={`admin-dist-select-${w.id}`}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <div className="w-10 h-10 flex items-center justify-center bg-[hsl(var(--secondary))] rounded-sm">
                                            {thumb ? (
                                                <img src={thumb} alt="" className="max-h-full max-w-full object-contain" loading="lazy" />
                                            ) : (
                                                <AcornPlaceholder size={24} opacity={0.55} />
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="text-sm">{w.name}</div>
                                        {w.denomination && (
                                            <div className="text-xs text-[hsl(var(--muted-foreground))]">{w.denomination}</div>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-sm">{w.producer}</TableCell>
                                    <TableCell>
                                        <span className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                                            {w.categoria || "—"}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        {w.alcohol_content != null
                                            ? <span className="text-sm">{w.alcohol_content}% vol</span>
                                            : <span className="text-[hsl(var(--muted-foreground))]">—</span>}
                                    </TableCell>
                                    <TableCell className="text-xs">{fmtSize(w) || "—"}</TableCell>
                                    <TableCell className="text-xs font-mono">
                                        {fmtSKU(w) || <span className="text-[hsl(var(--muted-foreground))]">—</span>}
                                    </TableCell>
                                    <TableCell className="text-sm">{fmtPrice(w)}</TableCell>
                                    <TableCell className="text-sm">{fmtStock(w)}</TableCell>
                                    <TableCell>
                                        <span className="co-badge-neutral">{VISIBILITY_LABEL[w.admin_visible_override]}</span>
                                    </TableCell>
                                    <TableCell>
                                        <span className="co-badge-neutral">{STATUS_LABEL[w.status]}</span>
                                    </TableCell>
                                    <TableCell className="text-xs text-[hsl(var(--muted-foreground))]">
                                        {SOURCE_LABEL[w.source] || w.source}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-1 flex-wrap">
                                            {w.status !== "validato_sommelier" && (
                                                <button
                                                    type="button"
                                                    onClick={() => validateSingle(w.id)}
                                                    disabled={validating}
                                                    className="co-btn-ghost text-xs px-2"
                                                    title="Valida"
                                                    data-testid={`admin-dist-validate-${w.id}`}
                                                >
                                                    <CheckCheck className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => searchImage(w.id)}
                                                disabled={imgSearching === w.id}
                                                className="co-btn-ghost text-xs px-2"
                                                title="Cerca immagine"
                                                data-testid={`admin-dist-image-search-${w.id}`}
                                            >
                                                <ImageIcon className="w-3.5 h-3.5" />
                                            </button>
                                            <Link
                                                to={`/admin/vini/${w.id}`}
                                                className="co-btn-ghost text-xs px-2"
                                                data-testid={`admin-dist-edit-${w.id}`}
                                            >
                                                <Pencil className="w-3.5 h-3.5" />
                                            </Link>
                                            <AlertDialog open={deleting === w.id} onOpenChange={(v) => !v && setDeleting(null)}>
                                                <AlertDialogTrigger asChild>
                                                    <button
                                                        type="button"
                                                        className="co-btn-ghost text-xs px-2 text-[hsl(var(--destructive))]"
                                                        onClick={() => setDeleting(w.id)}
                                                        data-testid={`admin-dist-delete-${w.id}`}
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Eliminare "{w.name}"?</AlertDialogTitle>
                                                        <AlertDialogDescription>Azione irreversibile.</AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                                                        <AlertDialogAction
                                                            onClick={() => doDelete(w.id)}
                                                            data-testid={`admin-dist-delete-confirm-${w.id}`}
                                                            className="bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90"
                                                        >
                                                            Elimina
                                                        </AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
